import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import useMyAccountQuery from '#hooks/queries/accounts/useMyAccountQuery';
import {
  AdminDailyScheduleTemplate,
  AdminWeeklyScheduleTemplate,
  ReservedMembersOfWeekItem
} from '#types/schedule';
import { ChipItem } from '../components/ChipButtons';
import useDateBasedColumns from '../hooks/useDateBasedColumns';
import { CalendarPeriodic, TimelineColumns } from '../types';
import { getIdByDate } from '../utils';
import * as S from './styles';

interface UseColumnsWeeklyParams {
  activeDate?: dayjs.Dayjs;
  coachId?: string;
  data?: AdminWeeklyScheduleTemplate[];
  calendarPeriodic: CalendarPeriodic;
}

interface UseColumnsDailyParams extends Omit<UseColumnsWeeklyParams, 'data'> {
  data?: AdminDailyScheduleTemplate[];
}

export function useChipOptions(data?: AdminWeeklyScheduleTemplate[]) {
  const { data: account } = useMyAccountQuery();
  const [chipList, setChipList] = useState<ChipItem[]>([]);

  useEffect(() => {
    if (account && data) {
      const _chipList: ChipItem[] = data?.map(item => ({
        text: account?.id === item.coachId ? '본인' : item.coachName,
        value: item.coachId
      }));

      setChipList(_chipList);
    }
  }, [data, account]);

  return chipList;
}

export function useColumnsWeekly({
  data,
  coachId,
  activeDate,
  calendarPeriodic
}: UseColumnsWeeklyParams): TimelineColumns[] {
  const [columnTemplate, setColumnTemplate] = useState<
    ReservedMembersOfWeekItem[]
  >([]);

  const columns = useDateBasedColumns({
    data: columnTemplate,
    activeDate,
    calendarPeriodic: 'WEEKLY'
  });

  const getWeeklyColumns = ({
    data,
    coachId,
    calendarPeriodic
  }: UseColumnsWeeklyParams) => {
    const columnsTemplate = data?.reduce((prev, curr) => {
      if (curr.coachId !== coachId && calendarPeriodic === 'WEEKLY') {
        return prev;
      }

      const reservedMembers = curr.reservedMembersOfWeek;
      const newState = [...prev];

      reservedMembers.forEach(item => {
        const id = getIdByDate(item.date, false);
        const index = newState.findIndex(
          prevItem => getIdByDate(prevItem.date, false) === id
        );

        if (index < 0) {
          newState.push(item);
        } else {
          newState[index].reservedMembers = [
            ...newState[index].reservedMembers,
            ...item.reservedMembers
          ];
        }
      });

      return newState;
    }, [] as ReservedMembersOfWeekItem[]);

    return columnsTemplate;
  };

  useEffect(() => {
    const newColumnsTemplate = getWeeklyColumns({
      data,
      coachId,
      calendarPeriodic,
      activeDate
    });
    setColumnTemplate(newColumnsTemplate ?? []);
  }, [data, coachId, calendarPeriodic, activeDate]);

  return columns;
}

export function useColumnsDaily({
  data,
  coachId,
  calendarPeriodic,
  activeDate
}: UseColumnsDailyParams): TimelineColumns[] {
  const { data: account } = useMyAccountQuery();
  const [columns, setColumns] = useState<TimelineColumns[]>([]);
  const columnWidth = data && data.length > 7 ? '14.4%' : undefined;

  const getDailyColumns = ({
    data,
    calendarPeriodic
  }: Omit<UseColumnsDailyParams, 'coachId'>) => {
    if (!data || calendarPeriodic !== 'DAILY') {
      return setColumns([]);
    }

    const newColumns = data.map<TimelineColumns>(item => {
      const isMe = account?.id === item.coachId;
      return {
        id: item.coachId,
        width: columnWidth,
        date: dayjs(activeDate),
        data: item.reservedMembers,
        isNotClickable: account?.id !== item.coachId,
        displayText() {
          return (
            <S.CoachNameHeader isMe={isMe}>
              {isMe ? '본인' : item.coachName}
            </S.CoachNameHeader>
          );
        }
      };
    });

    setColumns(newColumns);
  };

  useEffect(() => {
    getDailyColumns({ data, calendarPeriodic });
  }, [data, coachId, calendarPeriodic]);

  return columns;
}
